import React, { useState } from 'react';

import { Button, Flex, Loader, Space, Text, Tooltip, Variants } from '@mantine/core';
import { IconPlayerPause, IconPlayerPlay } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { SMART_ASSISTANT_QUERY_KEY } from 'shared/constants/query-keys';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';

interface PauseSmartAssistantButtonProps {
  smartAssistant: SmartAssistant;
  menuItem?: boolean;
  color?: string;
  borderColor?: string;
  disabled?: boolean;
  variant?: Variants<'gradient' | 'filled' | 'outline' | 'light' | 'white' | 'default' | 'subtle'> | undefined;
}

export function PauseSmartAssistantButton({
  smartAssistant,
  menuItem = false,
  color,
  borderColor,
  disabled,
  variant = 'outline'
}: PauseSmartAssistantButtonProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [isPaused, setIsPaused] = useState(smartAssistant.pausedIntegration ?? false);

  const togglePauseIntegration = useMutation({
    mutationFn: SmartAssistantService.togglePauseIntegration,
    onSuccess: (updatedSmartAssistant: SmartAssistant) => {
      queryClient.setQueryData([SMART_ASSISTANT_QUERY_KEY, { smartAssistantId: smartAssistant._id }], () => {
        return updatedSmartAssistant;
      });
      setIsPaused(updatedSmartAssistant.pausedIntegration ?? false);
      NotificationService.showResult(true, 'FEED_BACK.SMART_ASSISTANT.SUCCESS.TOGGLE_PAUSE_INTEGRATION');
    },
    onError: () => NotificationService.showResult(false, 'FEED_BACK.SMART_ASSISTANT.FAILURE.TOGGLE_PAUSE_INTEGRATION')
  });

  const onButtonClick = () => togglePauseIntegration.mutate(smartAssistant._id);

  return menuItem ? (
    <Flex
      direction="row"
      align="center"
      sx={(theme) => ({
        padding: theme.spacing.xs,
        '&:hover': {
          borderRadius: theme.radius.sm,
          backgroundColor: theme.colors.gray[1],
          cursor: 'pointer'
        }
      })}
      onClick={() => !togglePauseIntegration.isLoading && onButtonClick()}>
      {togglePauseIntegration.isLoading ? <Loader size="xs" /> : isPaused ? <IconPlayerPlay /> : <IconPlayerPause />}
      <Space w={10} />
      <Text>
        {isPaused ? t('SMART_ASSISTANT.PAUSE_INTEGRATION.RESUME') : t('SMART_ASSISTANT.PAUSE_INTEGRATION.PAUSE')}
      </Text>
    </Flex>
  ) : (
    <Tooltip disabled={isPaused} multiline maw={300} label={t('SMART_ASSISTANT.PAUSE_INTEGRATION.INFO')}>
      <Button
        sx={
          color && borderColor
            ? {
                color: color,
                borderColor: borderColor
              }
            : {}
        }
        variant={variant}
        disabled={disabled}
        onClick={onButtonClick}
        loading={togglePauseIntegration.isLoading}
        leftIcon={isPaused ? <IconPlayerPlay /> : <IconPlayerPause />}>
        {isPaused ? t('SMART_ASSISTANT.PAUSE_INTEGRATION.RESUME') : t('SMART_ASSISTANT.PAUSE_INTEGRATION.PAUSE')}
      </Button>
    </Tooltip>
  );
}
